import { FC, ReactNode } from 'react';
import {
    Accordion as MuiAccordion,
    AccordionSummary as MuiAccordionSummary,
    AccordionDetails as MuiAccordionDetails,
    Box,
    Typography,
    SxProps,
    TypographyProps,
} from '@mui/material';
import { ExpandMore } from '@mui/icons-material';
import { Theme } from '@mui/material/styles';

export interface IAccordionProps {
    title: ReactNode;
    variant?: 'default' | 'transparent';
    defaultExpanded?: boolean;
    extraIcon?: ReactNode;
    sx?: SxProps<Theme>;
    typographySx?: SxProps<Theme>;
    typographyProps?: TypographyProps;
    accordionSummarySx?: SxProps<Theme>;
    accordionDetailsSx?: SxProps<Theme>;
    disableGutters?: boolean;
    expanded?: boolean;
    testId?: string;
    onChange?: (event: React.SyntheticEvent, expanded: boolean) => void;
    disabled?: boolean;
}

export const Accordion: FC<IAccordionProps> = ({
    title,
    defaultExpanded,
    children,
    extraIcon,
    sx,
    typographySx,
    typographyProps,
    accordionSummarySx,
    disableGutters,
    expanded,
    variant = 'default',
    accordionDetailsSx,
    testId,
    onChange,
    disabled,
}) => {
    return (
        <MuiAccordion
            data-testid={testId}
            expanded={expanded}
            onChange={onChange}
            defaultExpanded={defaultExpanded}
            sx={[
                variant === 'transparent' && {
                    backgroundColor: 'transparent',
                    boxShadow: 'none',
                    '&::before': {
                        display: 'none',
                    },
                },
                ...(Array.isArray(sx) ? sx : [sx]),
            ]}
            disableGutters={disableGutters}
            disabled={disabled}
        >
            <MuiAccordionSummary
                aria-controls="panel1d-content"
                id="panel1d-header"
                expandIcon={<ExpandMore fontSize="large" />}
                sx={{ ...accordionSummarySx }}
            >
                {typeof title === 'string' ? (
                    <>
                        {/* eslint-disable react/jsx-props-no-spreading */}
                        <Typography
                            component="h3"
                            variant="h6"
                            sx={{ alignSelf: 'center', ...typographySx }}
                            {...typographyProps}
                        >
                            {title}
                        </Typography>
                    </>
                ) : (
                    title
                )}
                {extraIcon != null ? (
                    <Box
                        component="span"
                        onClick={(event) => event.stopPropagation()}
                        onMouseDown={(event) => event.stopPropagation()}
                        sx={{ display: 'inline-flex', alignItems: 'center' }}
                    >
                        {extraIcon}
                    </Box>
                ) : null}
            </MuiAccordionSummary>
            <MuiAccordionDetails sx={accordionDetailsSx}>{children}</MuiAccordionDetails>
        </MuiAccordion>
    );
};
